import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Lock, AlertCircle, Loader2 } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { Select } from '../../components/ui/Select'

type OperatingSystem = 'windows' | 'macos' | 'linux'
type Architecture = 'x64' | 'x86' | 'arm64'

interface DeploymentLinkData {
  id: string
  download_url: string
  deployment_type: 'windows' | 'macos' | 'linux'
  os_architecture: 'x64' | 'x86' | 'arm64'
  status: string
  expires_at: string
  device?: {
    name: string
    company: {
      name: string
    }
  }
}

const OS_OPTIONS: { label: string; value: OperatingSystem }[] = [
  { label: 'Windows', value: 'windows' },
  { label: 'macOS', value: 'macos' },
  { label: 'Linux', value: 'linux' }
]

const ARCHITECTURE_OPTIONS: Record<OperatingSystem, { label: string; value: Architecture }[]> = {
  windows: [
    { label: '64-bit', value: 'x64' },
    { label: '32-bit', value: 'x86' },
    { label: 'ARM64', value: 'arm64' }
  ],
  macos: [
    { label: 'Apple Silicon (ARM64)', value: 'arm64' },
    { label: 'Intel (x64)', value: 'x64' }
  ],
  linux: [
    { label: '64-bit', value: 'x64' },
    { label: 'ARM64', value: 'arm64' }
  ]
}

export const AgentDownloadPage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [deploymentLink, setDeploymentLink] = useState<DeploymentLinkData | null>(null)
  const [isDownloading, setIsDownloading] = useState(false)
  const [operatingSystem, setOperatingSystem] = useState<OperatingSystem>('windows')
  const [architecture, setArchitecture] = useState<Architecture>('x64')

  useEffect(() => {
    if (!token) {
      setError('Invalid download link - no token provided')
      setLoading(false)
      return
    }

    fetchDeploymentLink()
  }, [token])

  const fetchDeploymentLink = async () => {
    try {
      setLoading(true)

      const { data, error: fetchError } = await supabase
        .from('device_deployment_links')
        .select(`
          id,
          download_url,
          deployment_type,
          os_architecture,
          status,
          expires_at,
          devices!device_deployment_links_device_id_fkey (
            name,
            companies (
              name
            )
          )
        `)
        .eq('access_token', token)
        .single()

      if (fetchError || !data) {
        setError('Invalid or expired download link')
        setLoading(false)
        return
      }

      // Check if expired
      const expiresAt = new Date(data.expires_at)
      if (expiresAt < new Date()) {
        setError('This download link has expired')
        setLoading(false)
        return
      }

      // Transform the data structure
      const deviceData = Array.isArray(data.devices) ? data.devices[0] : data.devices
      const companyData = deviceData?.companies
      const companyInfo = Array.isArray(companyData) ? companyData[0] : companyData

      const transformedData: DeploymentLinkData = {
        id: data.id,
        download_url: data.download_url,
        deployment_type: data.deployment_type,
        os_architecture: data.os_architecture,
        status: data.status,
        expires_at: data.expires_at,
        device: deviceData ? {
          name: deviceData.name || 'Unknown Device',
          company: {
            name: companyInfo?.name || 'Unknown Company'
          }
        } : undefined
      }

      setDeploymentLink(transformedData)
      setOperatingSystem(transformedData.deployment_type)
      setArchitecture(transformedData.os_architecture)
      setLoading(false)
    } catch (err: any) {
      console.error('Error fetching deployment link:', err)
      setError('Failed to load download link')
      setLoading(false)
    }
  }

  const architectureOptions = useMemo(
    () => ARCHITECTURE_OPTIONS[operatingSystem],
    [operatingSystem]
  )

  const handleDownload = async () => {
    if (!deploymentLink || !token) return

    try {
      setIsDownloading(true)

      // Update deployment link status
      await supabase
        .from('device_deployment_links')
        .update({
          status: 'downloaded',
          downloaded_at: new Date().toISOString()
        })
        .eq('access_token', token)

      // Open download URL in new tab
      window.open(deploymentLink.download_url, '_blank', 'noopener,noreferrer')

      console.info('Agent download triggered', {
        operatingSystem,
        architecture,
        downloadUrl: deploymentLink.download_url
      })

      // Navigate to install instructions
      navigate(`/agent/install?os=${operatingSystem}&arch=${architecture}`)
    } catch (err: any) {
      console.error('Error downloading agent:', err)
      setError('Failed to start download')
    } finally {
      setIsDownloading(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8f9fb] flex flex-col items-center justify-center px-4">
        <Loader2 className="w-8 h-8 text-gray-600 animate-spin mb-4" />
        <p className="text-gray-600">Loading download...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#f8f9fb] flex flex-col items-center justify-center px-4">
        <div className="mb-10 flex flex-col items-center gap-3">
          <img src="/black_logo.svg" alt="Helpdesk 411" className="h-12 w-auto" />
        </div>
        <div className="w-full max-w-md rounded-3xl bg-white p-8 shadow-sm border border-gray-100">
          <div className="flex flex-col items-center text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Download Unavailable</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="rounded-full bg-gray-900 px-5 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-gray-800"
            >
              Go to Home
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (!deploymentLink) {
    return null
  }

  return (
    <div className="min-h-screen bg-[#f8f9fb] flex flex-col items-center justify-center px-4 py-12">
      <div className="mb-10 flex flex-col items-center gap-3">
        <img src="/black_logo.svg" alt="Helpdesk 411" className="h-12 w-auto" />
        <div className="flex items-center gap-1 text-sm text-gray-500">
          <span className="inline-flex h-2 w-2 rounded-full bg-gray-300" />
          <span className="inline-flex h-2 w-2 rounded-full bg-red-500" />
          <span className="inline-flex h-2 w-2 rounded-full bg-gray-300" />
        </div>
      </div>

      <div className="w-full max-w-md rounded-3xl bg-white p-8 shadow-sm border border-gray-100">
        <h1 className="text-2xl font-semibold text-gray-900 text-center mb-2">Download Agent</h1>

        {/* Device Info */}
        {deploymentLink.device && (
          <div className="text-center mb-6">
            <p className="text-sm text-gray-500">
              {deploymentLink.device.name} · {deploymentLink.device.company.name}
            </p>
          </div>
        )}

        <div className="space-y-5">
          {/* Locked OS Dropdown */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2 flex items-center gap-2">
              <Lock className="w-3 h-3" />
              Operating System
            </label>
            <Select
              value={operatingSystem}
              onChange={() => {}} // No-op
              disabled={true}
              className="w-full opacity-60 cursor-not-allowed"
            >
              {OS_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            <p className="text-xs text-gray-500 mt-1">Pre-configured by your administrator</p>
          </div>

          {/* Locked Architecture Dropdown */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2 flex items-center gap-2">
              <Lock className="w-3 h-3" />
              Architecture
            </label>
            <Select
              value={architecture}
              onChange={() => {}} // No-op
              disabled={true}
              className="w-full opacity-60 cursor-not-allowed"
            >
              {architectureOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            <p className="text-xs text-gray-500 mt-1">Pre-configured by your administrator</p>
          </div>

          {/* Download Button */}
          <button
            type="button"
            onClick={handleDownload}
            disabled={isDownloading}
            className="w-full rounded-full bg-gray-900 px-5 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isDownloading
              ? 'Starting download...'
              : `Download Agent (${operatingSystem === 'windows' ? 'Windows' : operatingSystem === 'macos' ? 'macOS' : 'Linux'} · ${architecture.toUpperCase()})`
            }
          </button>
        </div>

        {/* Install Instructions Link */}
        <div className="mt-6 flex items-center justify-center">
          <button
            type="button"
            className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900"
            onClick={() => navigate(`/agent/install?os=${operatingSystem}&arch=${architecture}`)}
          >
            Install Agent <span aria-hidden className="text-lg leading-none">→</span>
          </button>
        </div>
      </div>
    </div>
  )
}
